import { Injectable } from '@angular/core';
import { States } from '@class/commons/constants';
import { PermissionArea, PermissionKey } from '@class/commons/permissions/permission-constants';
import { ToolbarInterface } from '@class/commons/toolbar-interface';
import { Unit } from '@class/commons/unit.model';
import { HTTP_ERRORS_TRANSLATION_HEADING, generateHttpErrorMessage } from '@class/error/http-error-types';
import { AppHeaderService } from '@service/common/app-header.service';
import { FavKeys, FavouritesService } from '@service/common/favourites.service';
import { TranslationsService } from '@service/common/translations.service';
import { PermissionsService } from '@service/permissions/permissions.service';
import { Observable, catchError, map, of, shareReplay, startWith, tap } from 'rxjs';
import { UnitMqttStore } from './unit-mqtt.store';
import { ComponentStore } from '@ngrx/component-store';
import { UnitsApiService } from 'app/api-services/units/units.api-service';
import { HttpErrorResponse } from '@angular/common/http';
import { filterSuccessResult } from '@ngneat/query';

interface UnitStateData {
  data: Unit;
  tabs?: ToolbarInterface[];
  status: States;
  error?: string;
  message?: string;
}

const UNIT_INITIAL_STATE: UnitStateData = {
  status: States.INITIAL_LOADING,
  data: null,
};

@Injectable({
  providedIn: 'root',
})
export class UnitStore extends ComponentStore<UnitStateData> {
  get unit$() {
    return this.state$;
  }

  constructor(
    private _permissionsService: PermissionsService,
    private _headerService: AppHeaderService,
    private _favouritesService: FavouritesService,
    private _translationsService: TranslationsService,
    private _unitMqttStore: UnitMqttStore,
    private _unitsApiService: UnitsApiService,
  ) {
    super(UNIT_INITIAL_STATE);
  }

  getUnitById(unitId: string): void {
    this._unitsApiService
      .getUnit({ unitId })
      .result$.pipe(
        filterSuccessResult(),
        map((res) => res.data),
        shareReplay(),
        map((unit) => {
          const unitData = new Unit(unit);

          return {
            data: unitData,
            status: States.INITIAL_DATA_RECEIVED,
          };
        }),
        catchError((error) => this.handleError(error)),
        startWith(UNIT_INITIAL_STATE),
        tap((unitData) => {
          // this is hack
          if (unitData.data) {
            this._permissionsService.apply(PermissionArea.UNIT, unitData.data.permissions);
            this._headerService.setHeaderTitle(unitData.data.name);

            // set tabs perms and list
            const tabs = this.getTabs();

            // set back link
            // this.setHeaderBackLinkIfAccessible(unitData.data.portfolio_id);

            // kick off mqtt stream
            this._unitMqttStore.establishMqttForUnit(unitData.data);

            // add the portfolio in fav
            this._favouritesService.add(unitData.data, FavKeys.UNITS);

            this.setState({ ...unitData, tabs });
          } else {
            this.setState(unitData);
          }
        }),
      )
      .subscribe();
  }

  destroyUnitMqttStream(): void {
    this._unitMqttStore.destroyUnitMqttStream();
  }

  // private methods

  private getTabs(): ToolbarInterface[] {
    return [
      {
        name: 'overview',
        label: 'UnitPage.OverviewTabText',
        icon: 'stats-chart-outline',
        permissions: true,
      },
      {
        name: 'equipments',
        label: 'UnitPage.EquipmentTabText',
        icon: 'list-outline',
        permissions: this._permissionsService.any([
          PermissionKey.UNIT_VIEW_DEVICE,
          PermissionKey.UNIT_VIEW_CONTROLLER,
          PermissionKey.UNIT_ACCESS_TUNNELING,
        ]),
      },
      {
        name: 'alerts',
        label: 'Alerts.Alerts',
        icon: 'notifications-outline',
        permissions: this._permissionsService.any([
          PermissionKey.UNIT_VIEW_ALERT,
          PermissionKey.UNIT_VIEW_ALERTDEFINITION,
        ]),
      },
      {
        name: 'extras',
        label: 'UnitPage.ExtraTabText',
        icon: 'cash-outline',
        permissions: this._permissionsService.any([
          PermissionKey.UNIT_VIEW_UNIT_TARIFF,
          PermissionKey.UNIT_VIEW_EVENT,
          PermissionKey.UNIT_VIEW_LOGMESSAGE,
        ]),
      },
      {
        name: 'settings',
        label: 'UnitPage.SettingsTabText',
        icon: 'build-outline',
        permissions: this._permissionsService.any([
          PermissionKey.UNIT_CHANGE_UNIT,
          PermissionKey.UNIT_VIEW_UNITMEMBERSHIP,
        ]),
      },
    ];
  }

  // need to enable this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private setHeaderBackLinkIfAccessible(portfolioId: string): void {
    // Only update back default if
    // the navigation list is empty && user have permission to see parent portfolio
    // if (!this._routerOutlet.canGoBack() && this._permissionsService.all([PermissionKey.UNIT_VIEW_PORTFOLIO])) {
    //   this._headerService.setBackDefaultHref(`portfolio/${portfolioId}`);
    // }
  }

  private handleError(err: HttpErrorResponse, state: States = States.INITIAL_ERROR): Observable<UnitStateData> {
    const message = generateHttpErrorMessage(err, this._translationsService.instant(HTTP_ERRORS_TRANSLATION_HEADING));
    return of({
      status: state,
      error: message.message,
      data: null,
    });
  }
}
